import React from "react";
import { NewPropertyState } from "../../../usePropertyStateV2";
import { ComboBoxOptionType } from "../../components/input";
import { PropertyType } from "@/tailwind/tailwindUtils";
import { VariantDraggableInput } from "../../components/input/VariantDraggableInput";
import { SpacingIcon } from "../../components/icons";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface GapProps {
  columnGapState: NewPropertyState;
  rowGapState: NewPropertyState;
  flexDirectionState: NewPropertyState;
  updateInstantForProperty: (props: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const GapField: React.FC<GapProps> = React.memo(
  ({
    columnGapState,
    rowGapState,
    flexDirectionState,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
    updateInstantForProperty,
  }) => {
    const columnGapProperties = useTailwindProperties(PropertyType.COLUMN_GAP);
    const rowGapProperties = useTailwindProperties(PropertyType.ROW_GAP);

    return (
      <div className="flex flex-col items-start justify-between gap-1">
        <div className="text-xs text-text-secondary font-normal">Gap</div>
        <div className="w-full">
          {flexDirectionState.propertyValue?.startsWith("row") ? (
            <VariantDraggableInput
              labelContent={<SpacingIcon />}
              propertyState={columnGapState}
              comboboxOptions={columnGapProperties}
              buildArbitraryClass={(input: string) => {
                return {
                  tailwind: `gap-x-[${input}]`,
                  css: `column-gap: ${input}`,
                };
              }}
              step={0.5}
              min={0}
              fallback={{ number: 0, unit: "px" }}
              onSliderValueUpdate={(option, variant) =>
                updateInstantForProperty({
                  propertyType: PropertyType.COLUMN_GAP,
                  option,
                  setTemporaryDraggingValue:
                    columnGapState.setTemporaryDraggingValue,
                  variant,
                })
              }
              onClear={(variant?: string) => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.COLUMN_GAP,
                  setTemporaryDraggingValue:
                    columnGapState.setTemporaryDraggingValue,
                  variant,
                });
              }}
              onComboboxSelected={(option, variant) =>
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.COLUMN_GAP,
                  setTemporaryDraggingValue:
                    columnGapState.setTemporaryDraggingValue,
                  variant,
                })
              }
              tooltipContent="Column gap"
            />
          ) : (
            <VariantDraggableInput
              labelContent={<SpacingIcon />}
              propertyState={rowGapState}
              comboboxOptions={rowGapProperties}
              buildArbitraryClass={(input: string) => {
                return {
                  tailwind: `gap-y-[${input}]`,
                  css: `row-gap: ${input};`,
                };
              }}
              step={0.5}
              min={0}
              fallback={{ number: 0, unit: "px" }}
              onSliderValueUpdate={(option, variant) =>
                updateInstantForProperty({
                  propertyType: PropertyType.ROW_GAP,
                  option,
                  setTemporaryDraggingValue:
                    rowGapState.setTemporaryDraggingValue,
                  variant,
                })
              }
              onClear={(variant?: string) => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.ROW_GAP,
                  setTemporaryDraggingValue:
                    rowGapState.setTemporaryDraggingValue,
                  variant,
                });
              }}
              onComboboxSelected={(option, variant) =>
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.ROW_GAP,
                  setTemporaryDraggingValue:
                    rowGapState.setTemporaryDraggingValue,
                  variant,
                })
              }
              tooltipContent="Row gap"
            />
          )}
        </div>
      </div>
    );
  },
);

export default GapField;
